import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';
import Clipboard from 'clipboard/dist/clipboard.min';
import { removeClass, addClass, dataset, find, on } from '../helpers';
import { GaEvent } from '../EventTracking';

const contactEvent = ({ action, label }) => {
    return GaEvent({
        category: 'Contact',
        action,
        label,
    })
}

export default class PopupCore {
    constructor(element) {
        this.element = element;
        this.tippy = tippy(this.element, this.tippyConfig());
        this.copyButton = this.tippy.popper.querySelector('[data-copy-popup]');
        this.clipboard = new Clipboard(this.copyButton);

        this.listen();
    }

    listen() {
        this.element.addEventListener('click', e => e.preventDefault());
        this.clipboard.on('success', e => this.copySuccess(e));
    }

    tippyConfig() {
        return {
            theme: 'light',
            trigger: 'click',
            interactive: true,
            content: this.render(),
            allowHTML: true,
            onShow: () => {
                contactEvent({
                    action: 'Opened Dialog',
                    label: this.element.innerText,
                })
            },
            onHide: () => {
                contactEvent({
                    action: 'Closed Dialog',
                    label: this.element.innerText,
                })
            },
            onMount: instance => {
                if (!instance.eventsBound) {
                    instance.eventsBound = true;

                    const button = find('[data-action-link]', instance.popper);

                    on('click', () => {
                        contactEvent({
                            action: 'Single Click',
                            label: button.innerText,
                        })
                    }, button)
                }
            },
        }
    }

    copySuccess({ text }) {
        const defaultIcon = find('.clipboard-icon', this.copyButton);
        const successIcon = find('.clipboard-icon-success', this.copyButton);

        addClass(defaultIcon, 'hidden');
        removeClass(successIcon, 'hidden');

        setTimeout(() => {
            removeClass(defaultIcon, 'hidden');
            addClass(successIcon, 'hidden');
        }, 1200);

        contactEvent({
            action: 'Copied',
            label: text,
        })
    }

    getActionLink() {
        let link = 'javascript:alert(\'No data found.\');'

        if (this.element.href) {
            link = this.element.href;
        }

        if (dataset(this.element, 'actionValue')) {
            link = dataset(this.element, 'actionValue');
        }

        return link;
    }

    getActionText() {
        return dataset(this.element, 'actionText', this.element.innerText);
    }

    getActionIcon() {
        throw new Error('Please implement a getActionText method.');
    }

    getCopyData() {
        return dataset(this.element, 'copyValue', this.element.innerText);
    }

    getCopyText() {
        return dataset(this.element, 'copyText', `Copy ${this.element.innerText}`);
    }

    render() {
        const actionText = this.getActionText();

        if (actionText == null) {
            return `
                <div class="py-1">
                  <div class="pb-0.5">
                    <button data-copy-popup data-clipboard-text="${this.getCopyData()}" class="font-semibold py-2 px-4 hover:opacity-75 flex items-center">
                      <span class="clipboard-icon"><svg style="height: 15px; width: 15px;"  fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path></svg></span>
                      <span class="clipboard-icon-success hidden"><svg style="height: 15px; width: 15px;" fill="#65ce72" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path></svg></span>
                      <span class="pl-2">${this.getCopyText()}</span>
                    </button>
                  </div>
                </div>
            `;
        }

        return `
            <div class="py-1">
              <div class="pt-1">
                <a data-action-link href="${this.getActionLink()}" class="font-semibold py-2 px-4 hover:opacity-75 flex items-center">
                  <span>${this.getActionIcon()}</span>
                  <span class="pl-2">${this.getActionText()}</span>
                </a>
              </div>
              <div class="pb-0.5">
                <button data-copy-popup data-clipboard-text="${this.getCopyData()}" class="font-semibold py-2 px-4 hover:opacity-75 flex items-center">
                  <span class="clipboard-icon"><svg style="height: 15px; width: 15px;"  fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path></svg></span>
                  <span class="clipboard-icon-success hidden"><svg style="height: 15px; width: 15px;" fill="#65ce72" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path></svg></span>
                  <span class="pl-2">${this.getCopyText()}</span>
                </button>
              </div>
            </div>
        `;
    }
}
